import { z } from 'zod';
import { ResultLayoutOptions_S } from '../features/resultLayout/types/resultLayout.schema';

export const LabelsConfiguration_S = z.record(z.array(z.string()));

const pluginConfig = z.object({ name: z.string().optional() });

export const InstantSearchDisplayType_S = z.literal('rows').or(z.literal('grid'));

export const InstantSearchLayoutConfiguration_S = z.object({
    showSidebar: z.boolean(),
    showProductResults: z.boolean(),
    showCategoryResults: z.boolean(),
    showFulltextResults: z.boolean(),
    displayType: InstantSearchDisplayType_S,
    sidebarHorizontalPosition: z.literal('left').or(z.literal('right')),
    sidebarVerticalPosition: z.literal('top').or(z.literal('bottom')),
});

export const InstantSearchNativeElements_S = z.object({
    /**
     * A button / element which servers as a way to transition from instant search mode to complete SERP.
     */
    trigger: z.string().optional(),
    /**
     * The container which includes both search trigger and native search element (input). This is needed to set proper
     * z-index when using a dark backdrop behind the instant search modal.
     */
    inputContainer: z.string().optional(),
});

export const InstantSearchConfiguration_S = z.object({
    /**
     * If this is set to true, SERP will first open in instant search mode. Otherwise it will function in the default
     * mode.
     */
    useInstantSearch: z.boolean(),
    /**
     * Visual layout of the instant search modal.
     */
    layout: InstantSearchLayoutConfiguration_S.optional(),
    /**
     * Selectors for native elements of the site which are necessary for instant search functionalities.
     */
    nativeSearchElements: InstantSearchNativeElements_S.optional(),
});

export const NativeSearchAnalyticsInput_S = z.object({
    type: z.literal('input'),
    nativeInput: z.object({
        selectors: z.array(z.string()),
        event: z.string(),
    }),
});

export const NativeSearchAnalyticsConfig_S = z.object({
    enabled: z.boolean(),
    handlers: z.array(NativeSearchAnalyticsInput_S),
});

export const MobileLayout_S = z.enum(['tabs', 'allQuerysetsVisible']);

export const GlobalConfiguration_S = z.object({
    instantSearch: InstantSearchConfiguration_S.optional(),
    colorMode: z.literal('light').or(z.literal('dark')).or(z.string()).optional(),
    logoUrl: z.string(),
    logoLink: z.string().optional(),
    labels: LabelsConfiguration_S,
    gaMeasurementId: z.string().optional(),
    currency: z.string().optional(),
    locale: z.string().optional(),
    /**
     * Array of SERP plugin objects which specify plugin names.
     */
    plugins: z.union([pluginConfig, z.array(pluginConfig)]),
    /**
     * SERP visual theme based on which scss variables are selected.
     */
    theme: z.string(),
    deliveries: z.record(z.array(z.string())),
    forbidActionAnalytics: z.boolean().optional(),
    forbidGoogleAnalytics: z.boolean().optional(),
    showPriceWithVatSuffix: z.boolean(),
    /**
     * Debounce time for search bar input.
     */
    debounceTime: z.number(),
    /**
     * Whether or not we should display the add to cart button in the product card.
     */
    showAddToCartButton: z.boolean(),
    ABTesting: z
        .object({
            enabled: z.boolean(),
            nativeSearchAnalytics: NativeSearchAnalyticsConfig_S,
        })
        .optional(),
    resultLayout: z.object({
        defaultLayout: ResultLayoutOptions_S,
        showLayoutSwitcher: z.boolean(),
    }),
    mobileLayout: MobileLayout_S,
});
